import { Component, OnInit, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { News } from 'src/app/core/models/news';
import { Action } from 'src/app/core/models/action.model';

import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ClaimsService,
  ClaimWithCase,
} from 'src/app/core/services/claims.service';
import { CoolingOffPeriodService } from 'src/app/core/services/cooling-off-period.service';
import {
  CommunicationService,
  ThreadWithMessages,
  ThreadWithMessagesAndClaimCase,
} from 'src/app/core/services/communication.service';
import { NewsService } from 'src/app/core/services/news.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ConfirmModalComponent } from 'src/app/shared/components/simple/confirm-modal/confirm-modal.component';
import { InfoModalComponent } from 'src/app/shared/components/simple/info-modal/info-modal.component';
import {
  WithdrawFormData,
  WithdrawFormModalComponent,
} from '../../shared/simple/withdraw-form-modal/withdraw-form-modal.component';
import { WithdrawalFlowConfig } from 'src/app/core/models/withdrawal-config';
import { DocumentPreviewModalComponent } from 'src/app/shared/components/simple/document-preview-modal/document-preview-modal.component';
import { environment } from 'src/environments/environment';
import { VideoConfig, VideoItemConfig } from 'src/app/core/models/contract';
import {
  VideoModalComponent,
  VideoModalData,
  VideoModalResult,
} from '../../shared/smart/electronic-signature/components/eletronic-signature-modal/video-modal/video-modal.component';

@Component({
  templateUrl: './claim.component.html',
  styleUrls: ['./claim.component.scss'],
})
export class ClaimComponent implements OnInit {
  claim!: ClaimWithCase;

  comms$: BehaviorSubject<ThreadWithMessages[]> = new BehaviorSubject<
    ThreadWithMessages[]
  >([]);

  relatedNews: News | undefined = undefined;
  messages: ThreadWithMessagesAndClaimCase[] = [];
  alreadyAskedForWithdraw: boolean | null = null;
  isWithdrawing: boolean = false;

  canSeeOptions: boolean = false;

  isContractDateOver14DaysAgo: boolean = false;
  contractDate: string | null = null;
  withdrawalConfig: WithdrawalFlowConfig | null = null;
  readonly DATE_FORMAT = 'DD/MM/YYYY';
  readonly OIL_CASE_CODE = 'caso-vazamento-de-oleo';

  signedContractUrl: string | null = null;
  signedContractId: string | null = null;
  // CPK-4893: procuração (PoA) importada pelo escritório e já concluída (status DONE)
  poaImportId: string | null = null;
  contractConfiguration: any = null;
  contractId: string | null = null;
  contractPath: string | null = null;
  videoConfig?: VideoConfig;
  videoItems: VideoItemConfig[] = [];
  videoCompleted: boolean[] = [];
  videoProgress: number[] = [];

  get canSeeContractVideos(): boolean {
    return (
      this.claim?.cp_case?.code === this.OIL_CASE_CODE &&
      this.videoItems.length > 0
    );
  }

  constructor(
    private route: ActivatedRoute,
    private cs: ClaimsService,
    private cms: CommunicationService,
    private ns: NewsService,
    private router: Router,
    private dialog: MatDialog,
    private nts: NotificationService,
    public ts: TranslateService,
    private toastService: ToastService,
    private claimsService: ClaimsService,
    private http: HttpClient,
    private coolingOffPeriodService: CoolingOffPeriodService,
  ) { }

  async ngOnInit() {
    let idClaim = this.route.snapshot.paramMap.get('id');

    this.claim = await this.cs.getClaimWithCase(idClaim!);

    await this.getNews(idClaim!);

    const c = (
      await this.cms.listCommunicationsWithMessagesAndCaseForClaim(idClaim!)
    ).data;

    // Sort threads by the most recent message date (newest first)
    const sortedMessages = c.sort((a, b) => {
      const latestMessageA = this.getNewestMessageFromThread(a);
      const latestMessageB = this.getNewestMessageFromThread(b);

      if (!latestMessageA && !latestMessageB) return 0;
      if (!latestMessageA) return 1;
      if (!latestMessageB) return -1;

      return (
        new Date(latestMessageB.created_at!).getTime() -
        new Date(latestMessageA.created_at!).getTime()
      );
    });

    this.messages = sortedMessages;

    this.comms$.next(sortedMessages);

    const withdrawalRequests = await this.cs.getWithdrawalRequestsByClaimId(
      idClaim!
    );

    this.alreadyAskedForWithdraw = !!withdrawalRequests?.length;

    this._updateContractData();
    this._loadPowerOfAttorney();
  }

  private async _loadPowerOfAttorney() {
    // RLS (CLIENT_cp_client_import_view_own_claim) só devolve imports de claims do próprio cliente
    const { data, error } = await this.claimsService.supabase
      .from('cp_client_import')
      .select('id')
      .eq('claim_id', this.claim.id)
      .eq('status', 'DONE')
      .limit(1);

    if (error) {
      console.error('[CLAIM] Error fetching power of attorney:', error);
      return;
    }
    this.poaImportId = data?.[0]?.id ?? null;
  }

  private async _updateContractData() {
    // Fetch last signed contract for this claim
    // First, get the signed contract
    const { data: signedContracts, error: contractError } =
      await this.claimsService.supabase
        .from('cp_signed_contract')
        .select('id, created_at, signer_id, url, contract_id, status')
        .eq('claim_id', this.claim.id)
        .eq('status', 'SIGNED')
        .order('created_at', { ascending: false })
        .limit(1);

    if (contractError) {
      console.error('[CLAIM] Error fetching signed contract:', contractError);
    }

    if (signedContracts && signedContracts.length > 0) {
      const signedContract = signedContracts[0];
      this.signedContractId = signedContract.id;

      // Check if contract_id is valid
      if (!signedContract.contract_id) {
        console.error('[CLAIM] Signed contract has no contract_id');
        return;
      }

      // Set contract date early (only needs signedContract)
      const contractDateRaw = signedContract.created_at;
      const contractDateMoment = contractDateRaw ? moment(contractDateRaw) : null;
      this.contractDate = contractDateMoment ? contractDateMoment.format(this.DATE_FORMAT) : '';
      const coolingOff = await this.coolingOffPeriodService.getForClaimAndContract(
        this.claim,
        signedContract
      );
      this.isContractDateOver14DaysAgo = coolingOff.isOver14Days;
      const withdrawalType = this.isContractDateOver14DaysAgo ? 'termination' : 'cancellation';

      // Fetch contract data and withdrawal config in parallel
      const [{ data: contractData, error: contractConfigError }, withdrawalConfig] = await Promise.all([
        this.claimsService.supabase
          .from('cp_contract')
          .select('id, configuration, attributes')
          .eq('id', signedContract.contract_id)
          .single(),
        this.cs.getWithdrawalConfig(this.claim.id, withdrawalType).catch(() => null),
      ]);

      // Generate signed URL in background — does not block the button
      if (signedContract.url) {
        this.claimsService.supabase.storage
          .from('claim-documents')
          .createSignedUrl(signedContract.url, 3600)
          .then(({ data, error }) => {
            if (data) this.signedContractUrl = data.signedUrl;
            else console.error('[CLAIM] Error generating signed URL:', error);
          });
      }

      if (contractConfigError) {
        console.error('[CLAIM] Error fetching contract config:', contractConfigError);
      }

      if (contractData) {
        this.contractId = contractData.id;
        this.contractPath = signedContract.url;
        this.contractConfiguration = contractData.configuration;
        this.setupContractVideos();
      }

      this.withdrawalConfig = withdrawalConfig;
      this.canSeeOptions = true;
    }
  }

  async getNews(idCase: string) {
    const news = await this.ns.getNewsByRelatedCase(idCase, 1);

    this.relatedNews = news[0] ?? undefined;
  }

  getNewestMessageFromThread(thread: ThreadWithMessages) {
    // Sort messages and get the most recent
    return thread.cp_message?.sort((a, b) => {
      return (
        new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime()
      );
    })[0];
  }

  goToNewMessage() {
    this.router.navigate(['client/messages/new', this.claim.id]);
  }

  goToMessage(thread: string) {
    this.router.navigate(['client/messages/chat', thread]);
  }

  goAllMessages() {
    this.router.navigate(['client/messages']);
  }

  withdrawClaim() {
    if (this.isWithdrawing) {
      this.toastService.error(this.ts.instant('AlreadyWithdrawRequested'));
      return;
    }

    this.isWithdrawing = true;

    // Step 1: Confirmation
    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        titleIcon: 'delete',
        title: this.isContractDateOver14DaysAgo
          ? 'RequestToTerminate'
          : 'Withdraw Claim',
        message: this.isContractDateOver14DaysAgo
          ? 'Are you sure you want to request to terminate your claim?'
          : 'Are you sure you want to withdraw your claim?',
        confirmText: 'Yes, continue',
        cancelButton: 'Go back',
      },
      width: '400px',
    });

    confirmRef.afterClosed().subscribe(async (confirmed) => {
      if (!confirmed) {
        this.isWithdrawing = false;
        return;
      }

      const formRef = this.dialog.open(WithdrawFormModalComponent, {
        width: '600px',
        maxWidth: '90vw',
        disableClose: false,
        data: {
          claimId: this.claim.id,
          caseName: this.claim.cp_case?.summary ?? '',
          isContractDateOver14DaysAgo: this.isContractDateOver14DaysAgo,
          contractDate: this.contractDate ?? '',
          withdrawalConfig: this.withdrawalConfig,
        },
      });

      formRef.afterClosed().subscribe(async (formData?: WithdrawFormData) => {
        if (!formData) {
          this.isWithdrawing = false;
          return;
        }

        // Step 3: Submit with claimId + form data
        try {
          await this.cs.withdrawClaim(this.claim.id, formData);

          // Step 4: Success Modal
          const lang = this.ts.currentLang?.startsWith('pt') ? 'pt' : 'en';
          const configTitle = this.withdrawalConfig?.post_submission?.title;
          const configMessage = this.withdrawalConfig?.post_submission?.message;

          const infoModal = this.dialog.open(InfoModalComponent, {
            data: {
              title: configTitle
                ? (configTitle[lang as 'en' | 'pt'] || configTitle.en)
                : this.ts.instant('WithdrawTitle'),
              message: configMessage
                ? (configMessage[lang as 'en' | 'pt'] || configMessage.en)
                : this.ts.instant(
                    this.isContractDateOver14DaysAgo
                      ? 'TerminationRequestedMessage'
                      : 'CancellationRequestedMessage'
                  ),
              confirmText: this.ts.instant('GoToHomepage'),
            },
          });

          this.alreadyAskedForWithdraw = true;

          infoModal.afterClosed().subscribe(async () => {
            // Check if claim was inactivated after withdrawal
            try {
              const updatedClaim = await this.cs.getClaimWithCase(
                this.claim.id
              );
              if (updatedClaim.status === 'INACTIVE') {
                // Claim was inactivated after voluntary withdrawal, redirect with special flag
                this.router.navigate(['client'], {
                  queryParams: {
                    inactiveClaim: 'true',
                    inactiveClaimId: this.claim.id,
                    withdrawalType: this.isContractDateOver14DaysAgo ? 'termination' : 'cancellation'
                  },
                });
              } else {
                // Claim still active, just go to home
                this.router.navigate(['client']);
              }
            } catch (error) {
              // If error checking claim status, just navigate to home
              this.router.navigate(['client']);
            }
          });
        } catch (error) {
          this.toastService.error(this.ts.instant('Somethingwentwrong'));
        } finally {
          this.isWithdrawing = false;
        }
      });
    });
  }

  async viewContract() {
    if (!this.signedContractId) return;
    await this.openDocumentPreview(this.signedContractId, 'SIGNED_CONTRACT');
  }

  async downloadContract() {
    if (!this.signedContractId) return;
    await this.downloadDocument('signed_contract_id', this.signedContractId, `contract-${this.claim.id}.pdf`);
  }

  async viewPowerOfAttorney() {
    if (!this.poaImportId) return;
    await this.openDocumentPreview(this.poaImportId, 'POA');
  }

  async downloadPowerOfAttorney() {
    if (!this.poaImportId) return;
    await this.downloadDocument('poa_import_id', this.poaImportId, `power-of-attorney-${this.claim.id}.pdf`);
  }

  private async getAccessToken(): Promise<string | null> {
    const { data } = await this.claimsService.supabase.auth.getSession();
    return data?.session?.access_token ?? null;
  }

  private async openDocumentPreview(documentId: string, documentType: 'SIGNED_CONTRACT' | 'POA') {
    const token = await this.getAccessToken();
    if (!token) return;

    const lambdaUrl = environment.lambdaUrl.replace(/\/$/, '');
    this.dialog.open(DocumentPreviewModalComponent, {
      width: '90vw',
      maxWidth: '890px',
      height: '90vh',
      maxHeight: '90vh',
      data: {
        lambdaParams: {
          lambdaUrl,
          documentId,
          documentType,
          authToken: token,
        },
        isConfidential: true,
      },
    });
  }

  private async downloadDocument(idParam: 'signed_contract_id' | 'poa_import_id', documentId: string, filename: string) {
    const token = await this.getAccessToken();
    if (!token) return;

    const lambdaUrl = environment.lambdaUrl.replace(/\/$/, '');
    const url = `${lambdaUrl}/download-signed-documents?${idParam}=${documentId}&action=download`;

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const blob = await firstValueFrom(
      this.http.get(url, { headers, responseType: 'blob' })
    );

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  onActionsChanged(actions: Action[]) {
    console.log('[CLAIM] Actions changed', actions);

    if (this.canSeeOptions) {
      console.log('[CLAIM] Already allowed to see options, not updating contract data');
      return;
    }

    if (actions.some(action => action.action_type === 'SIGNATURE' && action.ac_status === 'SIGNED')) {
      console.log('[CLAIM] Signature action done, should update contract data');
      this._updateContractData();
    }
  }

  openContractVideosModal(templateRef: TemplateRef<unknown>): void {
    this.dialog.open(templateRef, {
      width: '90vw',
      maxWidth: '640px',
      panelClass: 'contract-videos-modal-panel',
    });
  }

  openContractVideo(index: number): void {
    const video = this.videoItems[index];
    if (!video) return;

    const ref = this.dialog.open(VideoModalComponent, {
      data: {
        video,
        controls: this.videoConfig?.controls,
        storageKey: this.getVideoStorageKey(index),
        claimId: this.claim.id,
        videoIndex: index,
      } as VideoModalData,
      width: '90vw',
      maxWidth: '800px',
      panelClass: 'video-modal-panel',
      disableClose: true,
    });

    ref.afterClosed().subscribe((result: VideoModalResult | undefined) => {
      if (!result) return;

      this.videoProgress[index] = result.progress;

      if (result.completed) {
        this.videoCompleted[index] = true;
        this.videoProgress[index] = 100;
      }
    });
  }

  private setupContractVideos(): void {
    if (this.claim?.cp_case?.code !== this.OIL_CASE_CODE) {
      this.videoConfig = undefined;
      this.videoItems = [];
      this.videoCompleted = [];
      this.videoProgress = [];
      return;
    }

    this.videoConfig = this.contractConfiguration?.video;
    this.videoItems = this.videoConfig?.videos ?? [];
    this.videoCompleted = this.videoItems.map((_, index) => {
      const saved = this.loadVideoProgress(index);
      return saved?.completed ?? false;
    });
    this.videoProgress = this.videoItems.map((_, index) => {
      const saved = this.loadVideoProgress(index);
      return saved?.progress ?? 0;
    });
  }

  private getVideoStorageKey(index: number): string {
    return `cp_contract_video_progress_${this.claim.id}_${index}`;
  }

  private loadVideoProgress(index: number): { progress: number; currentTime: number; completed: boolean } | null {
    try {
      const raw = localStorage.getItem(this.getVideoStorageKey(index));
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }
}
